import React, { Fragment, useEffect, useState, useMemo } from "react";
import ModalModern from "../../ModalModern";
import ModernPopupHeader from "../../common/ModernPopupHeader";
import ModernPopupBody from "../../common/ModernPopupBody";
import DataTable from "../../../table/DataTable"; // Adjust path to match your custom DataTable location
import { getSubscriptionDetails } from "../../../../../api/payments/subscriptionsAPI";
import LoadingPlaceholder from "../../../loading/LoadingPlaceholder";
import LoadingToRealTransitionWrapper from "../../../transitions/LoadingToRealTransitionWraper";
import GhostCardContainer from "../../../loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../loading/skeletons/GhostDataTable";
import GhostBar from "../../../loading/skeletons/GhostBar";
import { formatTimestampToLongString } from "../../../../../utils/date/DateUtils";
import Tooltip from "../../../Tooltip"; // Ajusta la ruta exacta si es necesario según tu árbol

function SubscriptionDetailsModal({ isOpen, onClose, subscription, copy }) {
  const [liveDetails, setLiveDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isOpen && subscription?.id) {
      setLoading(true);
      getSubscriptionDetails(subscription.id)
        .then((res) => res.json())
        .then((data) => {
          if (data?.subscription?.stripe_live_details) {
            setLiveDetails(data.subscription.stripe_live_details);
          }
        })
        .catch((err) =>
          console.error("Error loading live subscription details:", err),
        )
        .finally(() => setLoading(false));
    } else {
      setLiveDetails(null);
    }
  }, [isOpen, subscription]);

  // Cabeceras de la tabla de facturas
  const invoiceHeaders = useMemo(
    () => [
      {
        label: copy.invoice_date,
        key: "formatted_date",
        sortable: false,
        width: "120px",
      },
      {
        label: copy.invoice_amount,
        key: "amount",
        sortable: false,
        width: "140px",
      },
      {
        label: copy.invoice_status,
        key: "status",
        sortable: false,
        width: "120px",
        type: "payment_status",
      },
    ],
    [copy],
  );

  const getStatusOverrideStyle = (currentStatus) => {
    const normalized = currentStatus ? currentStatus.toUpperCase() : "";

    switch (normalized) {
      case "ACTIVE":
      case "PAID":
        return { backgroundColor: "#A6E510", color: "#00080F" };
      case "CANCELLED":
      case "CANCELED":
        return { backgroundColor: "#FFE5E5", color: "#CC0000" };
      case "NON-RENEWING":
      case "CANCELLING":
        return { backgroundColor: "#FFF3CD", color: "#856404" };
      case "DUE":
      case "PENDING":
        return { backgroundColor: "#FFD011", color: "#00080F" };
      case "FAILED":
        return { backgroundColor: "#FF6D4B", color: "#FFF" };
      default:
        return { backgroundColor: "#E6E6E7", color: "#666B6F" };
    }
  };

  const processedInvoices = useMemo(() => {
    if (!liveDetails?.invoices) return [];

    return liveDetails.invoices.map((inv) => {
      const rawInvStatus = inv.status ? inv.status.toUpperCase() : "";
      let displayStatusText = "Paid";

      if (rawInvStatus === "PENDING") {
        displayStatusText = "Pending";
      } else if (rawInvStatus === "FAILED") {
        displayStatusText = "Failed";
      } else if (rawInvStatus === "CANCELLED" || rawInvStatus === "CANCELED") {
        displayStatusText = "Cancelled";
      } else {
        displayStatusText = "Paid";
      }

      return {
        ...inv,
        formatted_date: formatTimestampToLongString(inv.date),
        status: displayStatusText,
      };
    });
  }, [liveDetails?.invoices]);

  if (!subscription || !isOpen) return null;

  const { service, status, period } = subscription;
  const isNonRenewing = (status || "").toUpperCase() === "NON-RENEWING";

  const pillLayoutStyle = {
    maxWidth: "120px",
    textAlign: "center",
    display: "inline-block",
  };

  return (
    <Fragment>
      <ModalModern onClose={onClose}>
        <ModernPopupHeader onClose={onClose}>
          <div className="header-content-modern-row">
            <h3 className="transaction-popup-title">{copy.title}</h3>
          </div>
        </ModernPopupHeader>
        <ModernPopupBody>
          <div className="mp-col-container" style={{ gap: "24px" }}>
            {/* Top Overview Segment Layout */}
            <div
              className="mp-row-container mobile-to-col padding-reset"
              style={{
                borderBottom: "1px solid var(--border-color)",
                paddingBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  flex: 1,
                }}
              >
                <div>
                  <span
                    className="type-pill"
                    style={{
                      ...pillLayoutStyle,
                      ...getStatusOverrideStyle(status),
                    }}
                  >
                    {isNonRenewing ? (
                      <Tooltip
                        title="Subscription Update"
                        content="This subscription has been canceled. You will retain full access to all features and associated data until the end of your paid billing cycle, after which it will not renew."
                      >
                        <span
                          style={{
                            borderBottom: "1px dashed #856404",
                            fontWeight: "600",
                            cursor: "help",
                          }}
                        >
                          {status}
                        </span>
                      </Tooltip>
                    ) : (
                      status
                    )}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "22px",
                    fontWeight: "600",
                    margin: 0,
                    lineHeight: "1.1",
                  }}
                >
                  {service}
                  <br></br>
                  <Tooltip content="The regular monthly subscription price before promotional discounts, coupons, or credits are applied.">
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                        color: "#004789",
                        borderBottom: "1px dashed #004789",
                      }}
                    >
                      ${subscription.raw_payload.amount} per Month
                    </span>
                  </Tooltip>
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "200px",
                }}
              >
                {loading ? (
                  <>
                    <GhostBar width="50%" height="40px" />
                    <GhostBar width="100%" height="15px" />
                    <GhostBar width="100%" height="15px" />
                    <GhostBar width="100%" height="15px" />
                  </>
                ) : (
                  liveDetails && (
                    <Fragment>
                      <span style={{ fontSize: "2rem", fontWeight: "700" }}>
                        {liveDetails.amount_to_charge}
                      </span>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "var(--grey-text)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <div
                          className="bc-settings-row"
                          style={{ alignItems: "center" }}
                        >
                          <span>{copy.payment_method}:</span>{" "}
                          <p style={{ fontSize: "14px" }}>
                            {liveDetails.payment_method}
                          </p>
                        </div>
                        <div
                          className="bc-settings-row"
                          style={{ alignItems: "center" }}
                        >
                          <span>{copy.next_payment}:</span>{" "}
                          <p style={{ fontSize: "14px" }}>
                            {formatTimestampToLongString(
                              liveDetails.next_payment_date,
                            )}
                          </p>
                        </div>
                        <div
                          className="bc-settings-row"
                          style={{ alignItems: "center" }}
                        >
                          <span>{copy.payment_cycle}:</span>{" "}
                          <p style={{ fontSize: "14px" }}>
                            {subscription.raw_payload.payment_interval}
                          </p>
                        </div>
                      </div>
                    </Fragment>
                  )
                )}
              </div>
            </div>

            {/* Invoices History Section */}
            <div className="invoice-history-section app-modal-datatable-override">
              <LoadingToRealTransitionWrapper
                isLoading={loading}
                ghost={
                  <GhostCardContainer>
                    <GhostDataTable
                      headers={invoiceHeaders}
                      rows={2}
                      title={true}
                      description={false}
                      showSearch={false}
                    />
                  </GhostCardContainer>
                }
              >
                <DataTable
                  title={copy.invoice_history_title}
                  headers={invoiceHeaders}
                  rows={processedInvoices}
                  getActionMenu={null}
                  searchQuery=""
                  filterElement={false}
                  showPagination={false}
                  emptyStateTitle={copy.no_invoices}
                  emptyStateText=""
                />
              </LoadingToRealTransitionWrapper>
            </div>
          </div>
        </ModernPopupBody>
      </ModalModern>
    </Fragment>
  );
}

export default SubscriptionDetailsModal;
